import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Search, Download, RefreshCw, Filter } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import './AuditLog.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: '',
    user_email: '',
    action: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit-logs`);
      setLogs(response.data || []);
    } catch (error) {
      console.error('Error al cargar bitácora:', error);
      toast.error('Error al cargar la bitácora');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...logs];

    if (filters.q) {
      const q = filters.q.toLowerCase();
      result = result.filter(log =>
        (log.user_email || '').toLowerCase().includes(q) ||
        (log.action || '').toLowerCase().includes(q) ||
        (log.details || '').toLowerCase().includes(q)
      );
    }

    if (filters.user_email) {
      const email = filters.user_email.toLowerCase();
      result = result.filter(log => (log.user_email || '').toLowerCase().includes(email));
    }

    if (filters.action) {
      result = result.filter(log => (log.action || '').includes(filters.action));
    }

    if (filters.date_from) {
      result = result.filter(log => (log.timestamp || '').slice(0, 10) >= filters.date_from);
    }

    if (filters.date_to) {
      result = result.filter(log => (log.timestamp || '').slice(0, 10) <= filters.date_to);
    }

    setFilteredLogs(result);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      q: '',
      user_email: '',
      action: '',
      date_from: '',
      date_to: ''
    });
  };

  const exportToExcel = () => {
    try {
      const data = filteredLogs.map(log => ({
        'Fecha/Hora': formatDateTime(log.timestamp),
        'Usuario': log.user_email || 'N/A',
        'Acción': formatAction(log.action),
        'IP': log.ip_address || 'N/A',
        'Detalles': log.details || ''
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bitácora');
      XLSX.writeFile(wb, `bitacora_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar a Excel');
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const formatAction = (action) => {
    const actions = {
      'LOGIN_SUCCESS': 'Inicio de sesión',
      'LOGIN_FAILED': 'Intento fallido de login',
      'CREATE_POST': 'Crear publicación',
      'UPDATE_POST': 'Actualizar publicación',
      'DELETE_POST': 'Eliminar publicación',
      'CREATE_USER': 'Crear usuario',
      'UPDATE_USER': 'Actualizar usuario',
      'DELETE_USER': 'Eliminar usuario'
    };
    return actions[action] || action;
  };

  const getActionBadgeClass = (action) => {
    if (action.includes('LOGIN_SUCCESS') || action.includes('CREATE')) return 'action-success';
    if (action.includes('DELETE')) return 'action-danger';
    if (action.includes('UPDATE')) return 'action-warning';
    if (action.includes('FAILED')) return 'action-error';
    return 'action-default';
  };

  return (
    <div className="audit-container fade-in" data-testid="audit-view">
      <div className="container">
        <div className="panel">
          <div className="panel-header-audit">
            <div>
              <h2 className="panel-title">
                <BookOpen size={24} />
                Bitácora de Auditoría
              </h2>
              <p className="audit-subtitle">
                Registro completo de acciones en el sistema
              </p>
            </div>
            <div className="audit-actions">
              <button
                className="btn btn-secondary btn-sm"
                onClick={fetchLogs}
                disabled={loading}
                data-testid="refresh-audit-btn"
              >
                <RefreshCw size={16} />
                Actualizar
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={exportToExcel}
                data-testid="export-audit-btn"
              >
                <Download size={16} />
                Exportar Excel
              </button>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="filters-header">
            <Filter size={20} />
            <h3>Filtros de Búsqueda</h3>
          </div>

          <div className="filters-grid-audit">
            <div className="form-group">
              <label>Búsqueda general</label>
              <input
                type="text"
                placeholder="Usuario, acción, detalles..."
                value={filters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
                data-testid="audit-search"
              />
            </div>

            <div className="form-group">
              <label>Usuario</label>
              <input
                type="email"
                placeholder="usuario@ejemplo.com"
                value={filters.user_email}
                onChange={(e) => handleFilterChange('user_email', e.target.value)}
                data-testid="audit-user-filter"
              />
            </div>

            <div className="form-group">
              <label>Acción</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                data-testid="audit-action-filter"
              >
                <option value="">Todas</option>
                <option value="LOGIN_SUCCESS">Inicio de sesión</option>
                <option value="LOGIN_FAILED">Intento fallido</option>
                <option value="CREATE_POST">Crear publicación</option>
                <option value="UPDATE_POST">Actualizar publicación</option>
                <option value="DELETE_POST">Eliminar publicación</option>
              </select>
            </div>

            <div className="form-group">
              <label>Desde</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                data-testid="audit-date-from"
              />
            </div>

            <div className="form-group">
              <label>Hasta</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                data-testid="audit-date-to"
              />
            </div>

            <div className="form-group">
              <label>&nbsp;</label>
              <button
                className="btn btn-secondary"
                onClick={clearFilters}
                data-testid="clear-audit-filters"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="audit-stats">
            <div className="stat-card">
              <div className="stat-number">{filteredLogs.length}</div>
              <div className="stat-label">Total de Registros</div>
            </div>
          </div>
        </div>

        <div className="panel table-panel">
          {loading ? (
            <div className="loading-state">
              <div className="loader"></div>
              <p>Cargando bitácora...</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="audit-table" data-testid="audit-table">
                <thead>
                  <tr>
                    <th>Fecha/Hora</th>
                    <th>Usuario</th>
                    <th>Acción</th>
                    <th>IP</th>
                    <th>Detalles</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        No se encontraron registros
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="date-cell">{formatDateTime(log.timestamp)}</td>
                        <td className="user-cell">{log.user_email || 'N/A'}</td>
                        <td>
                          <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                            {formatAction(log.action)}
                          </span>
                        </td>
                        <td className="ip-cell">{log.ip_address || 'N/A'}</td>
                        <td className="details-cell">{log.details || ''}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
